import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { History } from 'lucide-react'
import { useState } from 'react'
import { historyQuery, tillsQuery, type ShiftHead } from '@/api/cash'
import { DeskHeader } from '@/app/cash/desk-shell'
import { useCashClub } from '@/app/cash/use-cash-club'
import { useDeskNames, type DeskNames } from '@/app/cash/use-desk'
import { ZPanel } from '@/app/cash/z-panel'
import { useCompanyCaller } from '@/app/company'
import { NothingForRole } from '@/app/nothing-for-role'
import { canSeeTill, cashClubs, cashErrorText, clubDayTime, clubTime, differenceOf, differenceText } from '@/domain/cash'
import { errorText } from '@/domain/errors'
import { formatMoneyColumn } from '@/domain/pass-rules'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState, Page } from '@/ui/page'
import { SettingsSection } from '@/ui/settings'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/cash/history')({
  component: ShiftHistory,
})

/// «Каса → Історія змін»: the club's shifts, newest first — each with who
/// opened and closed it, the count against the expected cash and the
/// difference; a closed one opens its Z (§6.3). `cash ≥ view`.
function ShiftHistory() {
  const { orgId } = Route.useParams()
  const { access, caller } = useCompanyCaller(orgId)
  const names = useDeskNames(orgId)
  const clubs = cashClubs(names.branches, access)
  const { club, pick } = useCashClub(orgId, clubs)
  const tills = useQuery({ ...tillsQuery(orgId, club?.id ?? ''), enabled: club !== null })
  const tillId = tills.data?.[0]?.id ?? null
  const history = useQuery({ ...historyQuery(orgId, tillId ?? ''), enabled: tillId !== null })
  const [opened, setOpened] = useState<string | null>(null)

  if (!canSeeTill(caller)) return <NothingForRole />

  const failed = tills.error ?? history.error
  return (
    <Page>
      <DeskHeader orgId={orgId} clubs={clubs} club={club} onPick={pick} />
      <div className="flex max-w-[720px] flex-col gap-6">
        {failed ? (
          <InlineBanner kind="error" message={errorText(failed, cashErrorText(names.branches))} />
        ) : tills.isPending || (tillId !== null && history.isPending) ? (
          <LoadingBlock label={t.app.loading} />
        ) : !history.data || history.data.length === 0 ? (
          <EmptyState icon={<History />} title={t.cash.history.empty} message={t.cash.history.emptyMessage} />
        ) : (
          <SettingsSection id="shifts" title={club?.name ?? t.cash.history.title} meta={history.data.length}>
            {history.data.map((shift) => (
              <ShiftRow key={shift.id} shift={shift} names={names} onOpen={() => setOpened(shift.id)} />
            ))}
          </SettingsSection>
        )}
      </div>
      {opened ? <ZPanel orgId={orgId} shiftId={opened} names={names} onClose={() => setOpened(null)} /> : null}
    </Page>
  )
}

/// «Зміна 2 · 29 жовт., 07:50 – 21:05 · Олег Савчук», and on the right what
/// was counted against what was expected — a shortage in the danger colour.
const ShiftRow = ({ shift, names, onOpen }: { shift: ShiftHead; names: DeskNames; onOpen: () => void }) => {
  const close = shift.close
  const difference = close ? differenceOf(close.countedMinor, close.expectedMinor) : null
  const span = close
    ? t.cash.history.span(clubDayTime(shift.openedAt, shift.timeZone), clubTime(close.closedAt, shift.timeZone))
    : clubDayTime(shift.openedAt, shift.timeZone)
  const content = (
    <>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-callout font-semibold text-text">{t.cash.shift.title(shift.number)}</span>
        <span className="text-caption text-text-2 text-pretty">
          {span} · {names.person(shift.openedByUserId)}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end text-right">
        {close ? (
          <>
            <span className="text-callout font-medium text-text tabular">{formatMoneyColumn(close.countedMinor)}</span>
            <span
              className={cn(
                'text-caption font-medium tabular',
                difference?.kind === 'matches' ? 'text-text-2' : difference?.kind === 'shortage' ? 'text-danger' : 'text-warning',
              )}
            >
              {difference ? differenceText(difference) : null}
            </span>
          </>
        ) : (
          <span className="rounded-full px-2 py-0.5 text-caption font-semibold text-success tint-active">{t.cash.history.open}</span>
        )}
      </span>
    </>
  )
  return (
    <div className="compact-row relative" style={{ ['--row-inset' as string]: '16px' }}>
      {close ? (
        <button
          type="button"
          onClick={onOpen}
          className="flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--row-hover,var(--surface-2))]"
        >
          {content}
        </button>
      ) : (
        <div className="flex min-h-14 items-center gap-3 px-4 py-2.5">{content}</div>
      )}
    </div>
  )
}
